import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Removes trailing slashes from the URL path (except for root "/").
 * Fixes the issue where S3/CloudFront (or other CDN) adds a trailing slash
 * when users visit via referrer links or refresh (e.g. /pricing → /pricing/).
 *
 * Safe with S3 + CDN:
 * - We only run when pathname ends with "/" (and not "/" itself). After
 *   navigate(..., { replace: true }), the next location has no trailing slash,
 *   so the condition is false and we never run again → no loop.
 * - We use replace: true so the corrected URL doesn't add a history entry.
 * - Redirect is client-side only (History API); no extra request to S3/CDN.
 * - Ref guard ensures we only redirect once per trailing-slash path (e.g. if
 *   React Strict Mode runs the effect twice or CDN does something odd).
 */
const TrailingSlashRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    const shouldRedirect =
      pathname !== "/" &&
      pathname.endsWith("/") &&
      !hasRedirectedRef.current;

    if (shouldRedirect) {
      hasRedirectedRef.current = true;
      const normalizedPath = pathname.replace(/\/+$/, "") || "/";
      const newPath = normalizedPath + search + hash;
      navigate(newPath, { replace: true });
    }

    // Reset when pathname changes to a path we haven't seen (e.g. user navigated elsewhere)
    if (!pathname.endsWith("/")) {
      hasRedirectedRef.current = false;
    }
  }, [pathname, search, hash, navigate]);

  return null;
};

export default TrailingSlashRedirect;
